import { useParams, Link } from 'react-router-dom'
import { RiArrowLeftLine, RiTimeLine, RiCalendarLine } from 'react-icons/ri'
import SEO from '../../components/seo/SEO'
import { breadcrumb, article as articleLd } from '../../components/seo/jsonLd'

const POSTS = {
  'monsoon-haircare-chennai': {
    title: 'Monsoon haircare survival guide for Chennai humidity',
    excerpt: 'Frizz, flatness and breakage are the trio nobody asks for. Here is what our colourists actually use at home.',
    category: 'Hair',
    date: '2026-04-22',
    minutes: 5,
    body: `Chennai humidity tests every strand of hair. Our team's playbook is simple: cleanse less, hydrate more, and seal everything in. Below is the routine we share with every guest who walks in for a colour service in monsoon.

Step 1 — Switch to a sulphate-free shampoo and reduce washes to 2× a week. Sulphates strip the cuticle and amplify frizz. We currently recommend Kérastase Discipline or L'Oréal Vitamino Color Resveratrol depending on your hair type.

Step 2 — Mask, don't condition. Once a week, replace conditioner with a deep-treatment mask for 10 minutes. The molecular weight of mask actives is small enough to enter the cortex; conditioner mostly coats the cuticle.

Step 3 — Seal with a leave-in oil before stepping out. Two to three drops on the mid-lengths and ends are enough. Heavy hair? Argan or sweet-almond. Fine hair? Camellia or grapeseed.

Step 4 — Cool blast at the end of every blow-dry. The sudden temperature drop shrinks the cuticle and gives mirror-shine that survives a 6 PM commute through OMR traffic.

Step 5 — Sleep on satin or silk. Friction is the silent killer of monsoon-fresh blowouts. A simple pillowcase swap pays for itself in two weeks.

Want a personalised plan? Book a free 15-minute hair consultation with one of our seniors.`,
  },
  'bridal-skin-prep-90-days': {
    title: 'Your 90-day bridal skin prep checklist',
    excerpt: 'A week-by-week roadmap our skin therapists give every bride.',
    category: 'Bridal',
    date: '2026-04-12',
    minutes: 8,
    body: 'Three months out is when real results compound. Here is the timeline our brides follow…',
  },
}

export default function BlogPost() {
  const { slug } = useParams()
  const post = POSTS[slug]

  if (!post) {
    return (
      <>
        <SEO title="Post not found" path={`/blog/${slug}`} noIndex />
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
          <h1 className="font-display text-3xl text-salon-dark mb-3">We couldn't find that post.</h1>
          <Link to="/blog" className="btn-primary mt-4">Back to Journal</Link>
        </div>
      </>
    )
  }

  const dateStr = new Date(post.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${slug}`}
        type="article"
        jsonLd={[
          breadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/blog' },
            { name: post.title, url: `/blog/${slug}` },
          ]),
          articleLd({
            title: post.title,
            description: post.excerpt,
            slug,
            image: '/brand/banner-with-text.png',
            datePublished: post.date,
          }),
        ]}
      />

      <article className="bg-white">
        <header className="bg-hero-gradient py-24 text-center">
          <div className="container-salon max-w-3xl">
            <Link to="/blog" className="inline-flex items-center gap-1 text-salon-secondary text-sm font-body mb-4 hover:underline">
              <RiArrowLeftLine /> Back to Journal
            </Link>
            <span className="badge badge-gold mb-4">{post.category}</span>
            <h1 className="font-display text-3xl md:text-5xl text-white font-bold mb-4 leading-tight">{post.title}</h1>
            <div className="flex items-center justify-center gap-5 text-white/60 text-sm font-body">
              <span className="inline-flex items-center gap-1"><RiCalendarLine /> {dateStr}</span>
              <span className="inline-flex items-center gap-1"><RiTimeLine /> {post.minutes} min read</span>
            </div>
          </div>
        </header>

        <div className="container-salon max-w-3xl py-16">
          {post.body.split('\n\n').map((p, i) => (
            <p key={i} className="text-salon-dark font-body text-base sm:text-lg leading-relaxed mb-5">{p}</p>
          ))}

          <div className="mt-12 pt-8 border-t border-salon-cream-dark text-center">
            <p className="text-salon-muted font-body mb-4">Liked this? Read more on the journal.</p>
            <Link to="/blog" className="btn-secondary">Browse all articles</Link>
          </div>
        </div>
      </article>
    </>
  )
}
